// src/components/PredictionImage.tsx

import React, { useEffect, useRef, useState } from 'react'
import { AnnotationPredictionBoundingBox, Prediction } from '../types/prediction'
import { PredictionMetadata } from '../types/models/prediction'
import { Card } from 'react-bootstrap'
import PillsBBox from './PillsBBox'
import PillsClassification from './PillsClassification'
import { useContentImage } from '../hooks/useContentImage'
import { useAppSelector } from '../store/hooks'
import { selectSelectedProjectId } from '../store/slices/projectSlice'
import { AnnotationDataBoundingBox } from '../types/models/annotation'
import ImageCard from './annotation/ContentImageCard'
import { FaCheck } from 'react-icons/fa'
import styled from 'styled-components'
import { useGetProjectQuery } from '../services/projectsApi'

interface PredictionImageProps {
  prediction: Prediction
  onEdit: (prediction: Prediction) => void
  onToggle: (prediction: Prediction) => void
  isSelected: boolean
  bboxThreshold: number
}

const StyledCard = styled(Card)`
  width: 100%;
  height: 100%;
  cursor: pointer;
  position: relative;
  background: transparent;
  border: none;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
`

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  aspect-ratio: 1;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
`

const PredictionImage: React.FC<PredictionImageProps> = ({
  prediction,
  onEdit,
  onToggle,
  isSelected,
  bboxThreshold,
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const [imageBoundingBoxes, setImageBoundingBoxes] = useState<AnnotationDataBoundingBox[]>([])
  const [isHovered, setIsHovered] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const { base64Image, imageLoading, imageRef, contentData } = useContentImage({
    contentId: prediction.contentid,
    projectId: selectedProjectId || '',
    datasetId: project?.datasetid || '',
  })

  useEffect(() => {
    if (!contentData) return

    const bboxes = prediction?.predictions
      .map((predictionMeta: PredictionMetadata) => {
        const bboxData = predictionMeta.bounding_boxes || predictionMeta.oriented_bounding_boxes

        if (!bboxData) return null

        // Transform prediction coordinates from normalized (0-1) to pixel coordinates for UI
        let bbox: AnnotationDataBoundingBox

        if (bboxData.points && bboxData.points.length === 8) {
          // Case 1: Points exist (normalized 0-1), convert to pixel coordinates
          const pixelPoints = [
            bboxData.points[0] * contentData.width, // x1
            bboxData.points[1] * contentData.height, // y1
            bboxData.points[2] * contentData.width, // x2
            bboxData.points[3] * contentData.height, // y2
            bboxData.points[4] * contentData.width, // x3
            bboxData.points[5] * contentData.height, // y3
            bboxData.points[6] * contentData.width, // x4
            bboxData.points[7] * contentData.height, // y4
          ]

          // Calculate min/max from pixel coordinates
          const xValues = [pixelPoints[0], pixelPoints[2], pixelPoints[4], pixelPoints[6]]
          const yValues = [pixelPoints[1], pixelPoints[3], pixelPoints[5], pixelPoints[7]]

          bbox = {
            class_name: predictionMeta.class_name,
            points: pixelPoints, // Now in pixel coordinates for UI
            xmin: Math.min(...xValues),
            ymin: Math.min(...yValues),
            xmax: Math.max(...xValues),
            ymax: Math.max(...yValues),
            name: predictionMeta.tagid,
            confidence: parseFloat((predictionMeta?.confidence || 0).toFixed(2)),
            class_index: predictionMeta.class_index,
          } as AnnotationDataBoundingBox
        } else if (
          bboxData.xmin !== undefined &&
          bboxData.xmax !== undefined &&
          bboxData.ymin !== undefined &&
          bboxData.ymax !== undefined
        ) {
          // Case 2: Only min/max exists (normalized 0-1), convert to pixel coordinates and create points
          const xminPixel = bboxData.xmin * contentData.width
          const yminPixel = bboxData.ymin * contentData.height
          const xmaxPixel = bboxData.xmax * contentData.width
          const ymaxPixel = bboxData.ymax * contentData.height

          // Create points array from min/max (in pixel coordinates)
          const points = [
            xminPixel,
            yminPixel, // top-left
            xmaxPixel,
            yminPixel, // top-right
            xmaxPixel,
            ymaxPixel, // bottom-right
            xminPixel,
            ymaxPixel, // bottom-left
          ]

          bbox = {
            class_name: predictionMeta.class_name,
            points, // Pixel coordinates for UI
            xmin: xminPixel,
            ymin: yminPixel,
            xmax: xmaxPixel,
            ymax: ymaxPixel,
            name: predictionMeta.tagid,
            confidence: parseFloat((predictionMeta?.confidence || 0).toFixed(2)),
            class_index: predictionMeta.class_index,
          } as AnnotationDataBoundingBox
        } else {
          return null
        }

        return bbox
      })
      .filter((box): box is AnnotationDataBoundingBox => box !== null)

    // Show predictions at or above the threshold
    const filteredForConfidence = bboxes.filter(
      (box) => box?.confidence && box?.confidence >= bboxThreshold
    )
    const sortedForConfidence = filteredForConfidence.sort(
      (a, b) => (b.confidence || 0) - (a.confidence || 0)
    )
    setImageBoundingBoxes(sortedForConfidence)
  }, [prediction, bboxThreshold, contentData])

  const handleClick = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      onToggle(prediction)
    } else {
      onEdit(prediction)
    }
  }

  // Filter predictions based on confidence threshold (at or above)
  const filteredPredictions = prediction.predictions.filter(
    (pred) => pred.confidence >= bboxThreshold
  )

  // Don't render anything if no predictions meet or exceed the threshold
  if (filteredPredictions.length === 0) {
    return null
  }

  return (
    <StyledCard
      onClick={handleClick}
      className={isSelected ? 'selected' : ''}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ImageContainer ref={containerRef}>
        <div ref={imageRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
          {imageLoading && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1,
              }}
            >
              <div className="spinner-border spinner-border-sm" />
            </div>
          )}
          {base64Image && contentData && (
            <ImageCard
              base64Data={base64Image}
              showConfidence={false}
              bounding_boxes={imageBoundingBoxes}
              contentWidth={contentData.width}
              contentHeight={contentData.height}
              showBoundingBoxes={
                (project?.annotation_type === 'bounding_box' ||
                  project?.annotation_type === 'oriented_bounding_box') &&
                imageBoundingBoxes.length > 0
              }
              background="linear-gradient(135deg, #1a1a1a 0%, #252525 100%)"
              isSelected={isSelected}
              isHovered={isHovered}
            />
          )}

          {project?.annotation_type === 'classification' && (
            <div
              className="info-overlay"
              style={{
                position: 'absolute',
                bottom: '10px',
                left: '10px',
                zIndex: 1000,
              }}
            >
              <PillsClassification
                tagNames={[imageBoundingBoxes?.[0]?.class_name || '']}
                tagConfidence={[imageBoundingBoxes?.[0]?.confidence || 0]}
                className="prediction-text"
                maxTags={1}
                additionalCount={
                  imageBoundingBoxes.length > 1 ? imageBoundingBoxes.length - 1 : undefined
                }
              />
            </div>
          )}

          {project?.annotation_type === 'bounding_box' && (
            <div
              style={{
                position: 'absolute',
                bottom: '10px',
                left: '10px',
                zIndex: 1000,
              }}
            >
              <PillsBBox
                bounding_boxes={filteredPredictions.map((p, index) => {
                  return {
                    ...(p.bounding_boxes as AnnotationPredictionBoundingBox),
                    name: p.class_name,
                    confidence: p.confidence,
                    class_index: index,
                    ai_labeled: true,
                    points: [0, 0, 0, 0, 0, 0, 0, 0],
                  }
                })}
                maxTags={3}
              />
            </div>
          )}
        </div>
        {isSelected && (
          <div
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)',
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(147, 51, 234, 0.4)',
              border: '2px solid #ffffff',
              zIndex: 3,
            }}
          >
            <FaCheck color="white" size={14} />
          </div>
        )}
      </ImageContainer>
    </StyledCard>
  )
}

export default PredictionImage
